import { SidebarSimpleIcon } from "@phosphor-icons/react"
import { type ReactNode } from "react"
import { z } from "zod"

import { IconButton } from "~/components/design-system/components"
import SearchField from "~/components/planning/SearchField"
import { Card, CardContent, CardHeader, CardTitle } from "~/components/reusable/Card"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import { ShortcutTooltip } from "~/components/reusable/ShortcutTooltip"
import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"

const collapsedSchema = z.boolean()

type EntityListSidebarProps = {
  /** Heading shown in the rail, also used for the toggle's accessible name. */
  title: string
  searchPlaceholder: string
  /** Seeds the search field, which remounts and would otherwise lose the query on expand. */
  searchText: string
  onSearchTextChange: (searchText: string) => void
  isLoading?: boolean
  emptyMessage?: string
  /** True when the filtered list has no rows, so the rail can show `emptyMessage`. */
  isEmpty?: boolean
  /** Storage key for the collapsed state, unique per screen. */
  collapseStorageKey: string
  /** The list rows. */
  children: ReactNode
}

/**
 * Master list rail for the Customer Management screens: a heading, a search field and a
 * scrolling list of entities, collapsible to an icon strip so the detail pane can take the
 * full width on a narrow window.
 */
const EntityListSidebar = ({
  title,
  searchPlaceholder,
  searchText,
  onSearchTextChange,
  isLoading,
  emptyMessage,
  isEmpty,
  collapseStorageKey,
  children,
}: EntityListSidebarProps) => {
  const [collapsed, setCollapsed] = useSafeLocalStorage(
    collapseStorageKey,
    false,
    collapsedSchema,
    true,
  )

  if (collapsed) {
    return (
      <div className="flex h-full w-12 shrink-0 flex-col items-center border-r border-base py-3">
        <ShortcutTooltip label={`Show ${title.toLowerCase()}`} side="right">
          <IconButton
            variant="transparent"
            size="sm"
            aria-label={`Show ${title}`}
            onClick={() => setCollapsed(false)}
          >
            <SidebarSimpleIcon />
          </IconButton>
        </ShortcutTooltip>
      </div>
    )
  }

  return (
    <Card className="flex h-full w-[240px] shrink-0 flex-col overflow-hidden rounded-none border-r border-base shadow-none xl:w-[280px]">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <ShortcutTooltip label={`Hide ${title.toLowerCase()}`}>
          <IconButton
            variant="transparent"
            size="sm"
            aria-label={`Hide ${title}`}
            onClick={() => setCollapsed(true)}
          >
            <SidebarSimpleIcon />
          </IconButton>
        </ShortcutTooltip>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
        <SearchField
          containerClassName="w-full"
          placeholder={searchPlaceholder}
          onSearchTextChange={onSearchTextChange}
          initialSearchText={searchText}
        />

        {isLoading && <LoadingOverlay />}

        {!isLoading && isEmpty && emptyMessage && (
          <div className="label-base text-fg-subtle">{emptyMessage}</div>
        )}

        <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">{children}</div>
      </CardContent>
    </Card>
  )
}

export default EntityListSidebar
